import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Landmark, Receipt, ArrowRight } from 'lucide-react';

// Dummy salary data
var DUMMY_RECORDS = [
  { id: 1, empId: 'EMP001', name: 'Rahul Sharma', org: 'Zylker Corp', gross: 85000, retention: 0, netEligible: 85000, perDay: 2833, lopDays: 0, lopDed: 0, reward: 0, finalPayable: 85000, advanceDed: 5000, netPayable: 80000, itType: 'fixed', itAmount: 8000 },
  { id: 2, empId: 'EMP002', name: 'Priya Verma', org: 'Zylker Corp', gross: 65000, retention: 0, netEligible: 65000, perDay: 2167, lopDays: 2, lopDed: 4334, reward: 0, finalPayable: 60666, advanceDed: 0, netPayable: 60666, itType: 'percent', itPercent: 10, itAmount: 6067 },
  { id: 3, empId: 'EMP003', name: 'Amit Patel', org: 'Zylker Corp', gross: 120000, retention: 0, netEligible: 120000, perDay: 4000, lopDays: 0, lopDed: 0, reward: 2000, finalPayable: 122000, advanceDed: 10000, netPayable: 112000, itType: 'fixed', itAmount: 15000 },
  { id: 4, empId: 'EMP004', name: 'Sneha Gupta', org: 'Zylker Corp', gross: 50000, retention: 0, netEligible: 50000, perDay: 1667, lopDays: 0, lopDed: 0, reward: 0, finalPayable: 50000, advanceDed: 0, netPayable: 50000, itType: null, itAmount: 0 },
  { id: 5, empId: 'EMP005', name: 'Vikram Singh', org: 'Zylker Corp', gross: 95000, retention: 0, netEligible: 95000, perDay: 3167, lopDays: 1, lopDed: 3167, reward: 0, finalPayable: 91833, advanceDed: 3000, netPayable: 88833, itType: 'percent', itPercent: 12, itAmount: 10660 },
];

function fmt(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export default function ProtoIncomeTaxUI() {
  var recordsState = useState(DUMMY_RECORDS);
  var records = recordsState[0];
  var setRecords = recordsState[1];

  function handleITChange(id, field, value) {
    setRecords(function(prev) {
      return prev.map(function(r) {
        if (r.id !== id) return r;
        var updated = Object.assign({}, r);
        if (field === 'itType') {
          updated.itType = value;
          if (value === 'fixed') { updated.itAmount = 0; updated.itPercent = undefined; }
          if (value === 'percent') { updated.itPercent = 0; updated.itAmount = 0; }
          if (value === 'none') { updated.itType = null; updated.itAmount = 0; updated.itPercent = undefined; }
        }
        if (field === 'itAmount') { updated.itAmount = parseFloat(value) || 0; }
        if (field === 'itPercent') {
          updated.itPercent = parseFloat(value) || 0;
          updated.itAmount = Math.round(updated.netPayable * updated.itPercent / 100);
        }
        return updated;
      });
    });
  }

  var totalNetPayable = records.reduce(function(s, r) { return s + r.netPayable; }, 0);
  var totalIT = records.reduce(function(s, r) { return s + (r.itAmount || 0); }, 0);
  var totalBankTransfer = totalNetPayable - totalIT;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="proto-income-tax-page">
      <div className="max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Salary Entry - March 2026</h1>
            <p className="text-sm text-muted-foreground">Zylker Corp &middot; 5 employees</p>
          </div>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" variant="outline">
            STATIC PROTOTYPE
          </Badge>
        </div>

        {/* Main Salary Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="min-w-[50px]">#</TableHead>
                    <TableHead className="min-w-[160px]">Employee</TableHead>
                    <TableHead className="text-right min-w-[100px]">Gross</TableHead>
                    <TableHead className="text-center min-w-[60px]">LOP Days</TableHead>
                    <TableHead className="text-right min-w-[90px]">Absent Ded.</TableHead>
                    <TableHead className="text-right min-w-[100px]">Final Payable</TableHead>
                    <TableHead className="text-right min-w-[100px]">Advance Ded.</TableHead>
                    <TableHead className="text-right min-w-[120px] bg-indigo-50/50 border-l border-indigo-100">
                      <div className="flex items-center justify-end gap-1.5 text-indigo-700">
                        <Landmark className="h-3.5 w-3.5" />
                        Income Tax
                      </div>
                    </TableHead>
                    <TableHead className="text-right min-w-[120px] font-bold">Net Payable</TableHead>
                  </TableRow>
                </TableHeader>
                <ProtoTableBody records={records} onChange={handleITChange} />
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Income Tax Split Card — Separate Section */}
        <Card className="border-indigo-200 bg-white" data-testid="it-split-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-50">
                <Receipt className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Net Payable Breakdown</CardTitle>
                <p className="text-sm text-muted-foreground">Split of salary after Income Tax deduction</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="min-w-[50px]">#</TableHead>
                    <TableHead className="min-w-[160px]">Employee</TableHead>
                    <TableHead className="text-right min-w-[120px]">Net Payable</TableHead>
                    <TableHead className="text-center min-w-[130px] bg-indigo-50/50 border-l border-indigo-100">IT Type</TableHead>
                    <TableHead className="text-right min-w-[120px] bg-indigo-50/50">IT Amount</TableHead>
                    <TableHead className="text-right min-w-[140px]">
                      <div className="flex items-center justify-end gap-1.5 text-emerald-700">
                        <Landmark className="h-3.5 w-3.5" />
                        Bank Transfer
                      </div>
                    </TableHead>
                    <TableHead className="text-right min-w-[120px] text-indigo-700">
                      <div className="flex items-center justify-end gap-1.5">
                        <Receipt className="h-3.5 w-3.5" />
                        Tax Deducted
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <ProtoSplitBody records={records} />
                <tfoot>
                  <TableRow className="bg-slate-50 font-semibold border-t-2">
                    <TableCell colSpan={2} className="font-bold">TOTAL</TableCell>
                    <TableCell className="text-right font-mono">{fmt(totalNetPayable)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right font-mono text-indigo-700">{fmt(totalIT)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700 font-bold">{fmt(totalBankTransfer)}</TableCell>
                    <TableCell className="text-right font-mono text-indigo-700 font-bold">{fmt(totalIT)}</TableCell>
                  </TableRow>
                </tfoot>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ProtoTableBody({ records, onChange }) {
  return (
    <TableBody>
      {records.map(function(r, idx) {
        var bankTransfer = r.netPayable - (r.itAmount || 0);
        return (
          <TableRow key={r.id} className="hover:bg-slate-50/60">
            <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
            <TableCell>
              <div>
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.empId}</p>
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">{fmt(r.gross)}</TableCell>
            <TableCell className="text-center">{r.lopDays || '-'}</TableCell>
            <TableCell className="text-right font-mono text-red-600">{r.lopDed > 0 ? '-' + fmt(r.lopDed) : '-'}</TableCell>
            <TableCell className="text-right font-mono">{fmt(r.finalPayable)}</TableCell>
            <TableCell className="text-right font-mono text-orange-600">{r.advanceDed > 0 ? '-' + fmt(r.advanceDed) : '-'}</TableCell>
            <TableCell className="text-right bg-indigo-50/30 border-l border-indigo-100">
              {r.itType ? (
                <span className="font-mono text-indigo-700">-{fmt(r.itAmount)}</span>
              ) : (
                <span className="text-muted-foreground text-xs">N/A</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              {r.itAmount > 0 ? (
                <div>
                  <p className="font-mono font-semibold text-emerald-600">{fmt(bankTransfer)}</p>
                  <p className="text-xs text-indigo-600 font-mono">+ {fmt(r.itAmount)} tax</p>
                </div>
              ) : (
                <span className="font-mono font-semibold text-emerald-600">{fmt(r.netPayable)}</span>
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
}

function ProtoSplitBody({ records }) {
  return (
    <TableBody>
      {records.map(function(r, idx) {
        var bankTransfer = r.netPayable - (r.itAmount || 0);
        var hasIT = r.itType && r.itAmount > 0;

        return (
          <TableRow key={r.id} className="hover:bg-slate-50/60">
            <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
            <TableCell>
              <div>
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.empId}</p>
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">{fmt(r.netPayable)}</TableCell>
            <TableCell className="text-center bg-indigo-50/30 border-l border-indigo-100">
              {r.itType === 'fixed' && (
                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">Fixed</Badge>
              )}
              {r.itType === 'percent' && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">{r.itPercent}% of Net</Badge>
              )}
              {!r.itType && (
                <span className="text-xs text-muted-foreground">No IT</span>
              )}
            </TableCell>
            <TableCell className="text-right bg-indigo-50/30 font-mono text-indigo-700">
              {hasIT ? fmt(r.itAmount) : '-'}
            </TableCell>
            <TableCell className="text-right font-mono font-semibold text-emerald-700">
              {fmt(bankTransfer)}
            </TableCell>
            <TableCell className="text-right font-mono text-indigo-700">
              {hasIT ? fmt(r.itAmount) : '-'}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );
}
